import { useEffect, useRef, useState, useCallback } from 'react';
import Hls from 'hls.js';
import { HLS_LIVE_CONFIG } from './hlsTuning';
import { ensureHlsCookie, holdHlsCookie, HeadersFn } from './hlsCookie';

interface HlsPlayerProps {
  hlsUrl: string;
  deviceSn: string;
  displayName?: string;
  detectionEnabled?: boolean;
  apiUrl?: string;
  /** Session headers, to fetch the `/hls/` cookie nginx checks (hlsCookie.ts). */
  authHeaders?: HeadersFn;
  onError?: (msg: string) => void;
  onConnected?: () => void;
  onDisconnected?: () => void;
}

export default function HlsPlayer({
  hlsUrl, deviceSn, displayName, detectionEnabled, apiUrl, authHeaders,
  onError, onConnected, onDisconnected,
}: HlsPlayerProps) {
  const authHeadersRef = useRef(authHeaders);
  authHeadersRef.current = authHeaders;
  /** A refused request may be an expired cookie: refetch it before the next attempt. */
  const renewCookie = useCallback(async () => {
    const h = authHeadersRef.current;
    if (h) await ensureHlsCookie(apiUrl || '', h, true);
  }, [apiUrl]);
  const videoRef = useRef<HTMLVideoElement>(null);
  const hlsRef = useRef<Hls | null>(null);
  const retryTimerRef = useRef<number | null>(null);
  const networkRetryCount = useRef(0);
  const [status, setStatus] = useState<'connecting' | 'playing' | 'error'>('connecting');
  const [liveTime, setLiveTime] = useState('');

  const onErrorRef = useRef(onError);
  const onConnectedRef = useRef(onConnected);
  const onDisconnectedRef = useRef(onDisconnected);
  onErrorRef.current = onError;
  onConnectedRef.current = onConnected;
  onDisconnectedRef.current = onDisconnected;

  useEffect(() => {
    const tick = () => setLiveTime(new Date().toLocaleTimeString('en-GB', { hour12: false }));
    tick();
    const id = window.setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);

  const cleanup = useCallback(() => {
    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }
  }, []);

  const createHls = useCallback((video: HTMLVideoElement, fullUrl: string) => {
    cleanup();
    setStatus('connecting');

    // Every live-latency number lives in hlsTuning.ts, with the arithmetic that
    // makes them consistent and a test that parses rtmp/nginx.conf so the client
    // and server halves of the budget cannot drift apart. Do not inline them back.
    const hls = new Hls({ ...HLS_LIVE_CONFIG });
    hlsRef.current = hls;

    hls.loadSource(fullUrl);
    hls.attachMedia(video);

    hls.on(Hls.Events.MANIFEST_PARSED, () => {
      networkRetryCount.current = 0;
      setStatus('playing');
      video.play().catch(() => {});
      onConnectedRef.current?.();
    });

    hls.on(Hls.Events.ERROR, (_event, data) => {
      if (data.fatal) {
        if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
          networkRetryCount.current++;
          if (networkRetryCount.current <= 30) {
            setStatus('connecting');
            hls.destroy();
            hlsRef.current = null;
            const delay = Math.min(2000 * networkRetryCount.current, 5000);
            retryTimerRef.current = window.setTimeout(() => {
              void renewCookie().then(() => {
                if (videoRef.current) createHls(videoRef.current, fullUrl);
              });
            }, delay);
          } else {
            setStatus('error');
            onErrorRef.current?.('Stream unavailable after retries');
            onDisconnectedRef.current?.();
          }
        } else if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
          hls.recoverMediaError();
        } else {
          setStatus('error');
          onErrorRef.current?.('HLS fatal error');
          onDisconnectedRef.current?.();
        }
      }
    });
  }, [cleanup, renewCookie]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !hlsUrl) return;

    networkRetryCount.current = 0;
    const fullUrl = (apiUrl || '') + hlsUrl;
    let cancelled = false;
    let teardown: (() => void) | null = null;
    // Hold the /hls/ cookie for as long as this player lives, and have it BEFORE the first
    // request — nginx refuses every playlist and segment without it (hlsCookie.ts).
    const release = holdHlsCookie();

    const start = () => {
      if (cancelled) return;
      // Prefer hls.js over native — Chrome 150+ reports canPlayType for HLS
      // but its native implementation doesn't handle live streams properly.
      // Native fallback is only for Safari/iOS where MSE is unavailable.
      if (Hls.isSupported()) {
        createHls(video, fullUrl);
        teardown = () => { cleanup(); onDisconnectedRef.current?.(); };
        return;
      }

      if (video.canPlayType('application/vnd.apple.mpegurl')) {
        cleanup();
        setStatus('connecting');
        video.src = fullUrl;
        video.play().catch(() => {});

        const onPlaying = () => { setStatus('playing'); onConnectedRef.current?.(); };
        const onErrorEvt = () => {
          setStatus('connecting');
          retryTimerRef.current = window.setTimeout(() => {
            void renewCookie().then(() => {
              video.src = fullUrl;
              video.load();
              video.play().catch(() => {});
            });
          }, 3000);
        };
        video.addEventListener('playing', onPlaying);
        video.addEventListener('error', onErrorEvt);

        teardown = () => {
          video.removeEventListener('playing', onPlaying);
          video.removeEventListener('error', onErrorEvt);
          cleanup();
        };
        return;
      }

      setStatus('error');
      onErrorRef.current?.('HLS playback not supported in this browser');
    };

    const h = authHeadersRef.current;
    if (h) {
      // Resolves false on any failure — play anyway: a deployment whose nginx does not
      // check yet (or local dev, with no nginx) must keep working.
      void ensureHlsCookie(apiUrl || '', h).then(start);
    } else {
      start();
    }

    return () => {
      cancelled = true;
      release();
      teardown?.();
    };
  }, [hlsUrl, apiUrl, cleanup, createHls, renewCookie]);

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%', background: '#000' }}>
      <video
        ref={videoRef}
        autoPlay
        muted
        playsInline
        style={{ width: '100%', height: '100%', objectFit: 'contain' }}
      />

      {status === 'connecting' && (
        <div style={{
          position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center', color: 'var(--color-text-muted)', gap: 8,
        }}>
          <div style={{
            width: 24, height: 24, border: '2px solid #374151', borderTopColor: '#3b82f6',
            borderRadius: '50%', animation: 'spin 0.8s linear infinite',
          }} />
          <span style={{ fontSize: 13 }}>Waiting for stream...</span>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      )}

      {status === 'error' && (
        <div style={{
          position: 'absolute', inset: 0, display: 'flex', alignItems: 'center',
          justifyContent: 'center', color: 'var(--color-danger)', fontSize: 13,
        }}>
          Stream unavailable
        </div>
      )}

      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0,
        display: 'flex', justifyContent: 'space-between', padding: '6px 10px',
        background: 'linear-gradient(rgba(0,0,0,0.6), transparent)',
        pointerEvents: 'none',
      }}>
        <span style={{ color: '#fff', fontSize: 12, fontWeight: 600 }}>{displayName || deviceSn}</span>
        <span style={{ color: 'var(--color-text-secondary)', fontSize: 11 }}>{liveTime}</span>
      </div>
    </div>
  );
}
